import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import { Check, X, Eye, AlertCircle, Building2 } from 'lucide-react';

type PAMMFund = {
  id: string;
  name: string;
  manager: string;
  nav: number;
  investors: number;
  roi: number;
  managementFee: number;
  performanceFee: number;
  submittedDate: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
};

const AdminPAMMApproval = () => {
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'pending' | 'approved' | 'rejected' | 'suspended'
  >('pending');

  const funds: PAMMFund[] = [
    {
      id: 'PAM001',
      name: 'Alpha Growth Fund',
      manager: 'Mike Johnson',
      nav: 2300000,
      investors: 128,
      roi: 42.5,
      managementFee: 2.0,
      performanceFee: 20.0,
      submittedDate: '2024-01-15',
      status: 'pending',
    },
    {
      id: 'PAM002',
      name: 'FX Income Fund',
      manager: 'Sarah Williams',
      nav: 540000,
      investors: 45,
      roi: 28.3,
      managementFee: 1.5,
      performanceFee: 15.0,
      submittedDate: '2024-01-14',
      status: 'pending',
    },
    {
      id: 'PAM003',
      name: 'Conservative Growth',
      manager: 'David Brown',
      nav: 890000,
      investors: 67,
      roi: 18.7,
      managementFee: 1.0,
      performanceFee: 10.0,
      submittedDate: '2024-01-13',
      status: 'approved',
    },
    {
      id: 'PAM004',
      name: 'High Yield Fund',
      manager: 'Emily Chen',
      nav: 1200000,
      investors: 89,
      roi: 55.2,
      managementFee: 2.5,
      performanceFee: 25.0,
      submittedDate: '2024-01-12',
      status: 'pending',
    },
    {
      id: 'PAM005',
      name: 'Stable Income',
      manager: 'Robert Wilson',
      nav: 450000,
      investors: 34,
      roi: 12.4,
      managementFee: 0.5,
      performanceFee: 5.0,
      submittedDate: '2024-01-11',
      status: 'rejected',
    },
  ];

  const filteredFunds = funds.filter(
    fund => statusFilter === 'all' || fund.status === statusFilter
  );

  const handleApprove = (id: string) => {
    console.log('Approving PAMM fund:', id);
  };

  const handleReject = (id: string) => {
    console.log('Rejecting PAMM fund:', id);
  };

  const handleView = (id: string) => {
    console.log('Viewing PAMM fund:', id);
  };

  const fundColumns: DataTableColumn<PAMMFund>[] = [
    {
      id: 'id',
      header: 'ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '100px',
    },
    {
      id: 'name',
      header: 'Fund Name',
      accessor: row => (
        <div>
          <div className="font-semibold">{row.name}</div>
          <div className="text-sm text-muted-foreground">{row.manager}</div>
        </div>
      ),
      sortable: true,
      sortValue: row => row.name,
      width: '200px',
    },
    {
      id: 'nav',
      header: 'NAV',
      accessor: row => `$${(row.nav / 1000000).toFixed(2)}M`,
      sortable: true,
      sortValue: row => row.nav,
      width: '100px',
    },
    {
      id: 'investors',
      header: 'Investors',
      accessor: row => row.investors,
      sortable: true,
      sortValue: row => row.investors,
      width: '100px',
    },
    {
      id: 'roi',
      header: 'ROI',
      accessor: row => (
        <span className="font-semibold text-green-600">+{row.roi}%</span>
      ),
      sortable: true,
      sortValue: row => row.roi,
      width: '100px',
    },
    {
      id: 'fees',
      header: 'Fees',
      accessor: row => (
        <div className="text-sm">
          <div>Mgmt: {row.managementFee}%</div>
          <div>Perf: {row.performanceFee}%</div>
        </div>
      ),
      sortable: false,
      width: '120px',
    },
    {
      id: 'submittedDate',
      header: 'Submitted',
      accessor: row => new Date(row.submittedDate).toLocaleDateString(),
      sortable: true,
      sortValue: row => row.submittedDate,
      width: '120px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === 'approved'
              ? 'bg-green-100 text-green-800'
              : row.status === 'rejected'
                ? 'bg-red-100 text-red-800'
                : row.status === 'suspended'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => handleView(row.id)}>
            <Eye className="h-4 w-4" />
          </Button>
          {row.status === 'pending' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleApprove(row.id)}
                className="text-green-600 hover:text-green-700"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleReject(row.id)}
                className="text-red-600 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
      sortable: false,
      width: '120px',
    },
  ];

  const pendingCount = funds.filter(f => f.status === 'pending').length;
  const approvedCount = funds.filter(f => f.status === 'approved').length;
  const rejectedCount = funds.filter(f => f.status === 'rejected').length;
  const totalNAV = funds.reduce((sum, f) => sum + f.nav, 0);
  const totalInvestors = funds.reduce((sum, f) => sum + f.investors, 0);

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="PAMM Fund Approval"
          description="Review and approve PAMM fund applications"
        />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant={statusFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('all')}
        >
          All ({funds.length})
        </Button>
        <Button
          variant={statusFilter === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('pending')}
        >
          Pending ({pendingCount})
        </Button>
        <Button
          variant={statusFilter === 'approved' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('approved')}
        >
          Approved ({approvedCount})
        </Button>
        <Button
          variant={statusFilter === 'rejected' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('rejected')}
        >
          Rejected ({rejectedCount})
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{pendingCount}</div>
            <p className="text-sm text-muted-foreground mt-2">
              Funds awaiting approval
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Total NAV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              ${(totalNAV / 1000000).toFixed(2)}M
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Total fund value
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              Approved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{approvedCount}</div>
            <p className="text-sm text-muted-foreground mt-2">Active funds</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-purple-600" />
              Total Investors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalInvestors}</div>
            <p className="text-sm text-muted-foreground mt-2">
              Across all funds
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>PAMM Fund Applications</CardTitle>
          <CardDescription>
            Review and manage PAMM fund approval requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={fundColumns}
            data={filteredFunds}
            searchPlaceholder="Search funds..."
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPAMMApproval;
